import { useRef } from 'react'
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'

const EASE = [0.16, 1, 0.3, 1]

function Line({ children, delay, className = '' }) {
  return (
    <span className={`mask-line ${className}`}>
      <motion.span
        initial={{ y: '112%' }}
        animate={{ y: '0%' }}
        transition={{ duration: 1.05, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  )
}

export default function Hero() {
  const ref = useRef(null)
  const reduced = useReducedMotion()

  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.6, -2.6]), { stiffness: 60, damping: 18 })
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.6, 2.6]), { stiffness: 60, damping: 18 })

  const onMove = (e) => {
    if (reduced || !ref.current) return
    const r = ref.current.getBoundingClientRect()
    mx.set((e.clientX - r.left) / r.width - 0.5)
    my.set((e.clientY - r.top) / r.height - 0.5)
  }
  const onLeave = () => {
    mx.set(0)
    my.set(0)
  }

  const scrollToPhilosophy = () => {
    const el = document.getElementById('philosophy')
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 })
    else el?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-6 pb-14 pt-24"
      style={{ perspective: 1200 }}
      data-testid="hero-section"
    >
      {/* ambient pastel field */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <div
          className="blob absolute -top-[22%] left-[8%] h-[62vh] w-[62vh] rounded-full opacity-70"
          style={{ background: 'radial-gradient(circle at 40% 40%, rgba(177,201,232,0.55), rgba(177,201,232,0) 68%)', animationDuration: '19s' }}
        />
        <div
          className="blob absolute bottom-[-28%] right-[4%] h-[70vh] w-[70vh] rounded-full opacity-60"
          style={{ background: 'radial-gradient(circle at 55% 45%, rgba(203,216,233,0.6), rgba(203,216,233,0) 70%)', animationDuration: '23s', animationDelay: '-6s' }}
        />
        <div
          className="blob absolute left-[58%] top-[16%] h-[34vh] w-[34vh] rounded-full opacity-50"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(252,209,22,0.22), rgba(252,209,22,0) 70%)', animationDuration: '26s', animationDelay: '-11s' }}
        />
      </div>

      {/* soft packaging waves at the base */}
      <svg
        viewBox="0 0 1440 190"
        preserveAspectRatio="none"
        className="pointer-events-none absolute bottom-0 left-0 z-0 h-[16vh] w-full"
        aria-hidden="true"
        data-testid="hero-waves"
      >
        <path d="M0 96 C 240 160, 480 30, 720 78 C 960 126, 1200 38, 1440 86 L 1440 190 L 0 190 Z" fill="#F1EDE2" />
        <path d="M0 130 C 260 178, 520 74, 760 110 C 1000 146, 1240 82, 1440 122 L 1440 190 L 0 190 Z" fill="#CBD8E9" opacity="0.45" />
      </svg>

      {/* logo — the signature 3D reveal */}
      <motion.div style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }} className="relative z-10">
        <motion.img
          src="/logo-nmb.webp"
          alt="No More Bluff"
          data-testid="hero-logo"
          initial={{ clipPath: 'inset(0 0 100% 0)', scale: 1.08, opacity: 0.4 }}
          animate={{ clipPath: 'inset(0 0 0% 0)', scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: EASE, delay: 0.15 }}
          className="w-[min(72vw,470px)] select-none"
          draggable={false}
        />
      </motion.div>

      <div className="relative z-10 -mt-10 flex flex-col items-center text-center sm:-mt-14">
        <Line delay={0.85} className="mb-5">
          <span className="eyebrow inline-block" data-testid="hero-tagline">Built to keep you going.</span>
        </Line>

        <h1 className="display-hero max-w-[13ch] sm:max-w-[20ch]" data-testid="hero-statement">
          <Line delay={1.0}>Good habits shouldn't</Line>
          <Line delay={1.12}>
            feel like{' '}
            <span className="relative inline-block">
              hard work.
              <motion.svg
                viewBox="0 0 220 14"
                className="absolute -bottom-2 left-0 w-full"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 1.1, ease: EASE, delay: 1.9 }}
                aria-hidden="true"
              >
                <motion.path
                  d="M4 10 C 60 4, 150 4, 216 8"
                  fill="none"
                  stroke="#FF6B35"
                  strokeWidth="5"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.1, ease: EASE, delay: 1.9 }}
                />
              </motion.svg>
            </span>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.45 }}
          className="mt-5 max-w-md text-[0.98rem] leading-relaxed text-haze"
          data-testid="hero-supporting-copy"
        >
          We're building something simple for the everyday moments when you want to eat a little better, without overthinking it.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.6 }}
          className="mt-7 flex items-center gap-2.5 rounded-full border border-[var(--line)] bg-white/50 px-5 py-2 backdrop-blur-sm"
          data-testid="hero-coming-soon-badge"
        >
          <span className="breathe inline-block h-1.5 w-1.5 rounded-full bg-[#FCD116]" />
          <span className="eyebrow !tracking-[0.34em] !text-ink">Coming Soon</span>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.3 }}
        onClick={scrollToPhilosophy}
        className="group absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1.5 text-haze transition-colors hover:text-ink"
        aria-label="Scroll to learn more"
        data-testid="hero-scroll-cue"
      >
        <motion.span animate={{ y: [0, 5, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}>
          <ChevronDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  )
}
